/**
 * certificadoGenerator.js
 * Genera certificados profesionales de capacitación en PDF
 */

import { jsPDF } from 'jspdf';

/**
 * Genera un certificado profesional de capacitación aprobada
 * @param {Object} data - Datos del certificado
 * @param {string} data.nombreCompleto - Nombre completo del empleado
 * @param {string} data.legajo - Legajo del empleado
 * @param {string} data.nombreCapacitacion - Nombre de la capacitación
 * @param {string} data.categoriaCapacitacion - Categoría de la capacitación
 * @param {number} data.notaObtenida - Nota obtenida (%)
 * @param {string} data.fechaAprobacion - Fecha de aprobación
 * @param {string} data.duracionHoras - Duración en horas
 */
export const generarCertificado = async (data) => {
  const {
    nombreCompleto,
    legajo,
    nombreCapacitacion,
    categoriaCapacitacion,
    notaObtenida,
    fechaAprobacion,
    duracionHoras = '2'
  } = data;

  // Crear documento PDF en tamaño carta horizontal
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Colores elegantes
  const dorado = [184, 134, 11]; // Dorado oscuro #B8860B
  const doradoClaro = [218, 165, 32]; // Dorado claro #DAA520
  const grisOscuro = [64, 64, 64]; // Gris oscuro para texto
  const grisClaro = [128, 128, 128]; // Gris claro para subtítulos

  // ===================================
  // FONDO Y BORDES ELEGANTES
  // ===================================
  
  // Fondo crema muy suave
  doc.setFillColor(255, 253, 250);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  // Borde exterior dorado elegante
  doc.setDrawColor(...dorado);
  doc.setLineWidth(1.5);
  doc.rect(15, 15, pageWidth - 30, pageHeight - 30);

  // Borde interior dorado claro
  doc.setDrawColor(...doradoClaro);
  doc.setLineWidth(0.5);
  doc.rect(18, 18, pageWidth - 36, pageHeight - 36);

  // ===================================
  // DECORACIONES SUPERIORES (ESQUINAS)
  // ===================================
  
  // Decoración esquina superior izquierda - círculos concéntricos
  doc.setDrawColor(...dorado);
  doc.setLineWidth(1.5);
  doc.circle(25, 25, 8, 'S');
  doc.setLineWidth(1);
  doc.circle(25, 25, 11, 'S');
  doc.setLineWidth(0.5);
  doc.circle(25, 25, 14, 'S');
  
  // Decoración esquina superior derecha
  doc.setLineWidth(1.5);
  doc.circle(pageWidth - 25, 25, 8, 'S');
  doc.setLineWidth(1);
  doc.circle(pageWidth - 25, 25, 11, 'S');
  doc.setLineWidth(0.5);
  doc.circle(pageWidth - 25, 25, 14, 'S');

  // Líneas decorativas superiores
  doc.setLineWidth(0.5);
  doc.setDrawColor(...doradoClaro);
  doc.line(50, 25, pageWidth / 2 - 40, 25);
  doc.line(pageWidth / 2 + 40, 25, pageWidth - 50, 25);

  // ===================================
  // ENCABEZADO ELEGANTE
  // ===================================
  
  // Título principal "Reconocimiento" en estilo cursiva simulado
  doc.setTextColor(...dorado);
  doc.setFontSize(48);
  doc.setFont('times', 'italic');
  doc.text('Reconocimiento', pageWidth / 2, 50, { align: 'center' });

  // Subtítulo pequeño
  doc.setTextColor(...grisClaro);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('OTORGA EL PRESENTE RECONOCIMIENTO A', pageWidth / 2, 60, { align: 'center' });

  // ===================================
  // NOMBRE DEL DESTINATARIO (DESTACADO)
  // ===================================
  
  // Nombre en grande, elegante
  doc.setFontSize(36);
  doc.setFont('times', 'bold');
  doc.setTextColor(...grisOscuro);
  doc.text(nombreCompleto, pageWidth / 2, 85, { align: 'center' });

  // Línea decorativa dorada bajo el nombre
  doc.setDrawColor(...dorado);
  doc.setLineWidth(0.8);
  const nombreWidth = doc.getTextWidth(nombreCompleto);
  doc.line(
    pageWidth / 2 - nombreWidth / 2 - 10,
    88,
    pageWidth / 2 + nombreWidth / 2 + 10,
    88
  );

  // ===================================
  // CUERPO DEL TEXTO
  // ===================================
  
  // Texto central descriptivo
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grisClaro);
  const textoLinea1 = 'Por haber completado exitosamente el programa de capacitación en';
  doc.text(textoLinea1, pageWidth / 2, 105, { align: 'center' });

  // NOMBRE DE LA CAPACITACIÓN (destacado)
  doc.setFontSize(18);
  doc.setFont('times', 'bolditalic');
  doc.setTextColor(...dorado);
  doc.text(nombreCapacitacion, pageWidth / 2, 118, { align: 'center' });

  // Texto adicional
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grisClaro);
  doc.text(`Categoría: ${categoriaCapacitacion}`, pageWidth / 2, 128, { align: 'center' });
  
  doc.setFontSize(11);
  const textoLinea2 = `demostrando dedicación y excelencia profesional con una calificación de ${notaObtenida}%`;
  doc.text(textoLinea2, pageWidth / 2, 138, { align: 'center' });

  // ===================================
  // INFORMACIÓN ADICIONAL
  // ===================================
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grisClaro);
  doc.text(`Duración del programa: ${duracionHoras} horas | Legajo: ${legajo}`, pageWidth / 2, 148, { align: 'center' });

  // ===================================
  // PIE DE PÁGINA - FIRMAS
  // ===================================
  
  const footerY = pageHeight - 45;

  // Firma izquierda - Recursos Humanos
  doc.setDrawColor(...dorado);
  doc.setLineWidth(0.5);
  doc.line(40, footerY, 100, footerY);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grisOscuro);
  doc.text('Ricardo Santoro', 70, footerY + 5, { align: 'center' });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...grisClaro);
  doc.text('Director de Recursos Humanos', 70, footerY + 10, { align: 'center' });

  // Firma derecha - Director General
  doc.setDrawColor(...dorado);
  doc.line(pageWidth - 100, footerY, pageWidth - 40, footerY);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grisOscuro);
  doc.text('Marcelo Burgos', pageWidth - 70, footerY + 5, { align: 'center' });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...grisClaro);
  doc.text('Director General', pageWidth - 70, footerY + 10, { align: 'center' });

  // Fecha en el centro inferior
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...grisClaro);
  doc.text(`San Juan, ${fechaAprobacion}`, pageWidth / 2, footerY + 8, { align: 'center' });

  // ===================================
  // MEDALLA/SELLO DECORATIVO (Esquina inferior derecha)
  // ===================================
  
  // Círculo dorado grande
  doc.setFillColor(...dorado);
  doc.circle(pageWidth - 35, pageHeight - 35, 12, 'F');
  
  // Círculo interior más claro
  doc.setFillColor(...doradoClaro);
  doc.circle(pageWidth - 35, pageHeight - 35, 10, 'F');
  
  // Estrella simulada con texto
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('★', pageWidth - 35, pageHeight - 32, { align: 'center' });

  // ===================================
  // DECORACIONES INFERIORES
  // ===================================
  
  // Decoración esquina inferior izquierda - círculos concéntricos
  doc.setDrawColor(...dorado);
  doc.setLineWidth(1.5);
  doc.circle(25, pageHeight - 25, 8, 'S');
  doc.setLineWidth(1);
  doc.circle(25, pageHeight - 25, 11, 'S');
  doc.setLineWidth(0.5);
  doc.circle(25, pageHeight - 25, 14, 'S');
  
  // Líneas decorativas inferiores
  doc.setLineWidth(0.5);
  doc.setDrawColor(...doradoClaro);
  doc.line(50, pageHeight - 25, pageWidth / 2 - 50, pageHeight - 25);
  doc.line(pageWidth / 2 + 50, pageHeight - 25, pageWidth - 80, pageHeight - 25);

  // ===================================
  // NÚMERO DE CERTIFICADO
  // ===================================
  
  const codigoCertificado = `CERT-${legajo}-${Date.now()}`;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 180, 180);
  doc.text(`Código de certificado: ${codigoCertificado}`, pageWidth / 2, pageHeight - 8, { align: 'center' });

  // Generar nombre de archivo
  const nombreArchivo = `Certificado_${nombreCompleto.replace(/\s+/g, '_')}_${nombreCapacitacion.replace(/\s+/g, '_')}.pdf`;

  // Guardar PDF
  doc.save(nombreArchivo);

  return {
    success: true,
    nombreArchivo,
    codigoCertificado
  };
};

/**
 * Genera vista previa del certificado (retorna blob para mostrar en navegador)
 */
export const generarVistaPreviaCertificado = async (data) => {
  // Misma lógica pero retorna blob en lugar de descargar
  const doc = await generarCertificado(data);
  return doc.output('blob');
};
